import React, { useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Flex } from '@opendoor/bricks/core';
import { Sentry } from '@opendoor/observability';
import { Value } from 'classnames';
import moment from 'moment';

import {
  OdProtosListingsData_Listing_State as ListingState,
  OdProtosListingsData_PARNDidNotCloseReason as ParnDidNotCloseReason,
  OdProtosListingsData_PARNDidNotConvertReason as ParnDidNotConvertReason,
  OdProtosListingsData_PARN_State as ParnState,
} from '../../__generated__/athenaGlobalTypes';
import { useFirstContactAttemptedForParn } from '../../graphql/athena/mutations/FirstContactAttemptedForParn';
import { useFirstContactMadeForParn } from '../../graphql/athena/mutations/FirstContactMadeForParn';
import { useListingAgreementSignedForParn } from '../../graphql/athena/mutations/ListingAgreementSignedForParn';
import { useParnDidNotClose } from '../../graphql/athena/mutations/ParnDidNotClose';
import { useParnDidNotConvert } from '../../graphql/athena/mutations/ParnDidNotConvert';
import { useSetAgentNoteForParn } from '../../graphql/athena/mutations/SetAgentNoteForParn';
import { useSetListingDetails } from '../../graphql/athena/mutations/SetListingDetails';
import { InContractFields } from './InContractFields';
import { ListedFields } from './ListedFields';
import ParnDateField from './ParnDateField';
import ParnDropdownField, {
  PARN_LEAD_DID_NOT_CONVERT_OPTIONS,
  PARN_LISTING_DID_NOT_CLOSE_OPTIONS,
} from './ParnDropdownField';
import ParnTextAreaField from './ParnTextAreaField';
import {
  dateToDateStringForParn,
  didNotConvertOrCloseStates,
  getStatusTimestampForParn,
  preListingAgreementStates,
  priceToNumber,
  StateToUserStateForParn,
} from './parnUtils';
import { SoldFields } from './SoldFields';

type DateState = {
  [status: string]: Date | moment.Moment | undefined;
};

export type DropdownOptionTypes =
  | ParnState
  | ListingState
  | ParnDidNotConvertReason
  | ParnDidNotCloseReason;

interface OptionType {
  label: string;
  value: ParnState | ListingState;
  disabled?: boolean;
}

type statusKeys =
  | ParnState.LISTING_INITIATED
  | ParnState.FIRST_CONTACT_ATTEMPTED
  | ParnState.FIRST_CONTACT_MADE
  | ParnState.SELECTED_PARN
  | ListingState.LISTING_ACTIVE
  | ListingState.IN_CONTRACT
  | ListingState.SOLD;

export const statusTransitions: Record<statusKeys, (ParnState | ListingState)[]> = {
  [ParnState.LISTING_INITIATED]: [ParnState.LISTING_INITIATED, ParnState.FIRST_CONTACT_ATTEMPTED],
  [ParnState.FIRST_CONTACT_ATTEMPTED]: [
    ParnState.FIRST_CONTACT_ATTEMPTED,
    ParnState.FIRST_CONTACT_MADE,
    ParnState.UNKNOWN,
  ],
  [ParnState.FIRST_CONTACT_MADE]: [
    ParnState.FIRST_CONTACT_MADE,
    ParnState.SELECTED_PARN,
    ParnState.UNKNOWN,
  ],
  [ParnState.SELECTED_PARN]: [
    ParnState.SELECTED_PARN,
    ListingState.LISTING_ACTIVE,
    ListingState.UNKNOWN,
  ],
  [ListingState.LISTING_ACTIVE]: [
    ListingState.LISTING_ACTIVE,
    ListingState.IN_CONTRACT,
    ListingState.UNKNOWN,
  ],
  [ListingState.IN_CONTRACT]: [
    ListingState.IN_CONTRACT,
    ListingState.LISTING_ACTIVE,
    ListingState.SOLD,
    ListingState.UNKNOWN,
  ],
  [ListingState.SOLD]: [ListingState.SOLD],
};

interface ParnStatusFormProps {
  parnId: string;
  listingId: string;
  state: ParnState | ListingState;
  listingPrice?: number;
  contractPrice?: number;
  soldPrice?: number;
  grossCommission?: number;
  currentAgentNote: string;
  parnTimestampsMap: Map<string, string>;
  listingsTimestampsMap: Map<ListingState, string>;
}

export const ParnStatusForm: React.FC<ParnStatusFormProps> = ({
  parnId,
  listingId,
  state,
  listingPrice,
  contractPrice,
  soldPrice,
  grossCommission,
  currentAgentNote,
  parnTimestampsMap,
  listingsTimestampsMap,
}) => {
  const dateTimestamp: DateState = {
    [state]:
      getStatusTimestampForParn(state, parnTimestampsMap, listingsTimestampsMap) || undefined,
  };
  const [selectedParnStatusOption, setSelectedParnStatusOption] = useState(state);
  /** The listing service's UpdateMasks require snake case */
  const [listingPriceData, setListingPriceData] = useState({
    listing_price: listingPrice,
  });
  const [inContractPriceData, setInContractPriceData] = useState({
    contract_price: contractPrice,
  });
  const [soldPriceData, setSoldPriceData] = useState({
    sold_price: soldPrice,
    gross_commission: grossCommission,
  });
  const [agentNoteData, setAgentNoteData] = useState(currentAgentNote);

  const [date, setDate] = useState<DateState>(dateTimestamp);
  const [selectedParnLeadDidNotConvertOption, setSelectedParnLeadDidNotConvertOption] = useState(
    ParnDidNotConvertReason.UNKNOWN_REASON_DID_NOT_CONVERT,
  );
  const [selectedParnListingDidNotCloseOption, setSelectedParnListingDidNotCloseOption] = useState(
    ParnDidNotCloseReason.UNKNOWN_REASON_DID_NOT_CLOSE,
  );
  const [inputError, setInputError] = useState('Error updating status');

  const {
    setFirstContactAttemptedForParn,
    error: errorFirstContactAttempted,
    loading: loadingFirstContactAttempted,
  } = useFirstContactAttemptedForParn();
  const {
    setFirstContactMadeForParn,
    error: errorFirstContactMade,
    loading: loadingFirstContactMade,
  } = useFirstContactMadeForParn();
  const {
    setListingAgreementSignedForParn,
    error: errorListingAgreementSigned,
    loading: loadingListingAgreementSigned,
  } = useListingAgreementSignedForParn();
  const {
    setListingDetails,
    error: errorSetListingDetails,
    loading: loadingSetListingDetails,
  } = useSetListingDetails();
  const { setAgentNoteForParn, error: errorSetAgentNote } = useSetAgentNoteForParn();
  const { setParnDidNotConvert, error: errorParnDidNotConvert } = useParnDidNotConvert();
  const { setParnDidNotClose, error: errorParnDidNotClose } = useParnDidNotClose();

  const nextStatuses = statusTransitions[state as statusKeys] || [];

  const PARN_STATUS_OPTIONS: OptionType[] = nextStatuses.map((status) => ({
    label: StateToUserStateForParn[status],
    value: status,
    disabled: status === selectedParnStatusOption,
  }));

  const errorMap = [
    errorFirstContactAttempted,
    errorFirstContactMade,
    errorListingAgreementSigned,
    errorSetListingDetails,
    errorSetAgentNote,
    errorParnDidNotConvert,
    errorParnDidNotClose,
  ];

  Object.entries(errorMap).forEach(([_, error]) => {
    if (error) {
      Sentry.withScope((scope) => {
        scope.setExtra('parn_id', parnId);
        scope.setExtra('listing_id', listingId);
        scope.setExtra('error', JSON.stringify(error));
        Sentry.captureException(error);
      });
    }
  });

  //Helps determine whether to display menu of reasons lead didn't convert or reasons listing didn't close
  const wasPreListingAgreementSigned = preListingAgreementStates.includes(state);

  const handleParnStatusOptionChange = (option: DropdownOptionTypes) => {
    if (option in ParnState || option in ListingState) {
      setSelectedParnStatusOption(option as ParnState | ListingState);
    }
    setDate((prevTimestamps) => {
      const newTimestamps = { ...prevTimestamps };
      //Hide prior listing values in UI if contract fell through
      if (option === ListingState.LISTING_ACTIVE && state === ListingState.IN_CONTRACT) {
        newTimestamps[ListingState.LISTING_ACTIVE] = undefined;
        setListingPriceData((prevFormData) => ({
          ...prevFormData,
          listing_price: undefined,
        }));
      }
      //Hide prior contract values in UI if goes into contract after a prior contract fell through
      if (
        option === ListingState.IN_CONTRACT &&
        listingsTimestampsMap.has(ListingState.IN_CONTRACT) &&
        state === ListingState.LISTING_ACTIVE
      ) {
        newTimestamps[ListingState.IN_CONTRACT] = undefined;
        setInContractPriceData((prevFormData) => ({
          ...prevFormData,
          contract_price: undefined,
        }));
      }
      return newTimestamps;
    });
  };

  const handleDateChange = (date: moment.Moment | Date) => {
    const selectedDate = moment(date);
    const now = moment();
    selectedDate.hours(now.hours());
    selectedDate.minutes(now.minutes());
    selectedDate.seconds(now.seconds());
    setDate((prevDates) => ({ ...prevDates, [selectedParnStatusOption]: selectedDate }));
  };

  const handlePriceDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numberValue = priceToNumber(value);

    switch (selectedParnStatusOption) {
      case ListingState.LISTING_ACTIVE:
        setListingPriceData((prevFormData) => ({
          ...prevFormData,
          [name]: numberValue,
        }));
        break;
      case ListingState.IN_CONTRACT:
        setInContractPriceData((prevFormData) => ({
          ...prevFormData,
          [name]: numberValue,
        }));
        break;
      case ListingState.SOLD:
        setSoldPriceData((prevFormData) => ({
          ...prevFormData,
          [name]: numberValue,
        }));
        break;
    }
    if (value && !Number.isInteger(numberValue)) {
      setInputError('Please make sure you entered numbers as whole dollars without decimals');
    } else {
      setInputError('Error updating status');
    }
  };

  const handleAgentNoteChange = (
    _event: React.ChangeEvent<HTMLTextAreaElement>,
    _name: string,
    value?: Value,
  ) => {
    setAgentNoteData((value as string) ?? null);
  };

  const handleParnLeadDidNotConvertOptionChange = (option: DropdownOptionTypes) => {
    if (option in ParnDidNotConvertReason) {
      setSelectedParnLeadDidNotConvertOption(option as ParnDidNotConvertReason);
    }
  };

  const handleParnListingDidNotCloseOptionChange = (option: DropdownOptionTypes) => {
    if (option in ParnDidNotCloseReason) {
      setSelectedParnListingDidNotCloseOption(option as ParnDidNotCloseReason);
    }
  };

  const fieldRequirementsMet =
    state !== selectedParnStatusOption &&
    ((selectedParnStatusOption === ParnState.FIRST_CONTACT_ATTEMPTED &&
      date[ParnState.FIRST_CONTACT_ATTEMPTED]) ||
      (selectedParnStatusOption === ParnState.FIRST_CONTACT_MADE &&
        date[ParnState.FIRST_CONTACT_MADE]) ||
      (selectedParnStatusOption === ParnState.SELECTED_PARN && date[ParnState.SELECTED_PARN]) ||
      (selectedParnStatusOption === ListingState.LISTING_ACTIVE &&
        date[ListingState.LISTING_ACTIVE] &&
        listingPriceData.listing_price !== listingPrice) ||
      (selectedParnStatusOption === ListingState.IN_CONTRACT &&
        date[ListingState.IN_CONTRACT] &&
        inContractPriceData.contract_price !== contractPrice) ||
      (selectedParnStatusOption === ListingState.SOLD &&
        date[ListingState.SOLD] &&
        soldPriceData.sold_price !== soldPrice &&
        soldPriceData.gross_commission !== grossCommission) ||
      (selectedParnStatusOption === ParnState.UNKNOWN &&
        (selectedParnLeadDidNotConvertOption !==
          ParnDidNotConvertReason.UNKNOWN_REASON_DID_NOT_CONVERT ||
          selectedParnListingDidNotCloseOption !==
            ParnDidNotCloseReason.UNKNOWN_REASON_DID_NOT_CLOSE)));

  const handleSubmitParnStatusUpdate = async () => {
    try {
      await setAgentNoteForParn(parnId, agentNoteData);

      switch (selectedParnStatusOption) {
        case ParnState.FIRST_CONTACT_ATTEMPTED:
          await setFirstContactAttemptedForParn(
            parnId,
            dateToDateStringForParn(date[ParnState.FIRST_CONTACT_ATTEMPTED]),
          );
          break;
        case ParnState.FIRST_CONTACT_MADE:
          await setFirstContactMadeForParn(
            parnId,
            dateToDateStringForParn(date[ParnState.FIRST_CONTACT_MADE]),
          );
          break;
        case ParnState.SELECTED_PARN:
          await setListingAgreementSignedForParn(
            parnId,
            dateToDateStringForParn(date[ParnState.SELECTED_PARN]),
          );
          break;
        case ListingState.LISTING_ACTIVE:
          await setListingDetails({
            params: {
              id: listingId,
              listingPrice: Number(listingPriceData.listing_price),
              nextState: ListingState.LISTING_ACTIVE,
              updateMask: {
                paths: ['listing_price', 'next_state', 'timestamp_overrides'],
              },
              timestampOverrides: [
                {
                  state: ListingState.LISTING_ACTIVE,
                  timestamp: dateToDateStringForParn(date[ListingState.LISTING_ACTIVE]),
                },
              ],
            },
          });
          break;
        case ListingState.IN_CONTRACT:
          await setListingDetails({
            params: {
              id: listingId,
              contractPrice: Number(inContractPriceData.contract_price),
              nextState: ListingState.IN_CONTRACT,
              updateMask: {
                paths: ['contract_price', 'next_state', 'timestamp_overrides'],
              },
              timestampOverrides: [
                {
                  state: ListingState.IN_CONTRACT,
                  timestamp: dateToDateStringForParn(date[ListingState.IN_CONTRACT]),
                },
              ],
            },
          });
          break;
        case ListingState.SOLD:
          await setListingDetails({
            params: {
              id: listingId,
              soldPrice: Number(soldPriceData.sold_price),
              grossCommission: Number(soldPriceData.gross_commission),
              nextState: ListingState.SOLD,
              updateMask: {
                paths: ['sold_price', 'gross_commission', 'next_state', 'timestamp_overrides'],
              },
              timestampOverrides: [
                {
                  state: ListingState.SOLD,
                  timestamp: dateToDateStringForParn(date[ListingState.SOLD]),
                },
              ],
            },
          });
          break;
        case ParnState.UNKNOWN:
          if (
            selectedParnListingDidNotCloseOption !==
            ParnDidNotCloseReason.UNKNOWN_REASON_DID_NOT_CLOSE
          ) {
            await setParnDidNotClose(parnId, selectedParnListingDidNotCloseOption);
          }
          if (
            selectedParnLeadDidNotConvertOption !==
            ParnDidNotConvertReason.UNKNOWN_REASON_DID_NOT_CONVERT
          ) {
            await setParnDidNotConvert(parnId, selectedParnLeadDidNotConvertOption);
          }
          break;
      }
      showToast('info', 'Successfully updated lead', 'parn-dashboard-toast', {
        toastOptions: { autoClose: 3000 },
      });
    } catch (error) {
      showToast('error', inputError, 'parn-dashboard-toast', {
        toastOptions: { autoClose: false },
      });
    }
  };

  return (
    <Flex flexDirection="column" alignItems="start" padding="0px">
      {!didNotConvertOrCloseStates.includes(state) && (
        <ParnDropdownField
          id="status"
          title="Status"
          ariaLabel="Status"
          placeholder="Select an option"
          value={selectedParnStatusOption}
          currentState={state}
          optionHandler={handleParnStatusOptionChange}
          options={PARN_STATUS_OPTIONS}
        />
      )}
      {selectedParnStatusOption === ParnState.FIRST_CONTACT_ATTEMPTED && (
        <ParnDateField
          id="first_contact_attempted_on"
          label="First contact attempted on"
          value={date[ParnState.FIRST_CONTACT_ATTEMPTED]}
          onChangeHandler={handleDateChange}
        />
      )}
      {selectedParnStatusOption === ParnState.FIRST_CONTACT_MADE && (
        <ParnDateField
          id="first_contact_made_on"
          label="First contact made on"
          value={date[ParnState.FIRST_CONTACT_MADE]}
          onChangeHandler={handleDateChange}
        />
      )}
      {selectedParnStatusOption === ParnState.SELECTED_PARN && (
        <ParnDateField
          id="listing_agreement_signed_on"
          label="Listing agreement signed on"
          value={date[ParnState.SELECTED_PARN]}
          onChangeHandler={handleDateChange}
        />
      )}
      {selectedParnStatusOption === ListingState.LISTING_ACTIVE && (
        <ListedFields
          date={date[ListingState.LISTING_ACTIVE]}
          handleDateChange={handleDateChange}
          priceData={listingPriceData}
          handlePriceDataChange={handlePriceDataChange}
        />
      )}
      {selectedParnStatusOption === ListingState.IN_CONTRACT && (
        <InContractFields
          date={date[ListingState.IN_CONTRACT]}
          handleDateChange={handleDateChange}
          priceData={inContractPriceData}
          handlePriceDataChange={handlePriceDataChange}
        />
      )}
      {selectedParnStatusOption === ListingState.SOLD && (
        <SoldFields
          date={date[ListingState.SOLD]}
          handleDateChange={handleDateChange}
          priceData={soldPriceData}
          handlePriceDataChange={handlePriceDataChange}
        />
      )}
      <ParnTextAreaField
        title={didNotConvertOrCloseStates.includes(state) ? 'Agent note' : 'Set agent note'}
        id="parn_agent_note"
        ariaLabel="Set agent note"
        content={agentNoteData}
        onChangeHandler={handleAgentNoteChange}
        name="parn_agent_note"
        placeholder="Add a detailed note"
        analyticsName="parn-agent-note"
      />
      {selectedParnStatusOption === ParnState.UNKNOWN && wasPreListingAgreementSigned && (
        <ParnDropdownField
          id="reason-did-not-convert"
          title="Reason if lead did not convert"
          ariaLabel="Reason if lead did not convert"
          placeholder="Select a reason"
          value={selectedParnLeadDidNotConvertOption}
          optionHandler={handleParnLeadDidNotConvertOptionChange}
          options={PARN_LEAD_DID_NOT_CONVERT_OPTIONS}
          currentState={state}
        />
      )}
      {selectedParnStatusOption === ParnState.UNKNOWN && !wasPreListingAgreementSigned && (
        <ParnDropdownField
          id="reason-did-not-close"
          title="Reason if listing did not close"
          ariaLabel="Reason if listing did not close"
          placeholder="Select a reason"
          value={selectedParnListingDidNotCloseOption}
          optionHandler={handleParnListingDidNotCloseOptionChange}
          options={PARN_LISTING_DID_NOT_CLOSE_OPTIONS}
          currentState={state}
        />
      )}
      {didNotConvertOrCloseStates.includes(state) ? null : (
        <Button
          id="parn-status-update-button"
          size="md"
          variant="primary"
          aria-label="Submit Parn Status Update"
          analyticsName="submit-parn-update"
          onClick={handleSubmitParnStatusUpdate}
          disabled={!fieldRequirementsMet}
          maxWidth={'327px'}
          loading={
            loadingFirstContactAttempted ||
            loadingFirstContactMade ||
            loadingListingAgreementSigned ||
            loadingSetListingDetails
          }
          mt={4}
        >
          Update
        </Button>
      )}
    </Flex>
  );
};
